import {useMemo} from "react";
import {Bar, BarChart, CartesianGrid, XAxis, YAxis} from "recharts";

import {Card, CardHeader, CardTitle} from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";

import {formatBucketLong, formatBucketTick, TIME_SERIES_COLORS} from "../lib";
import type {DashboardEmailTimeSeries} from "../types";

type EmailTimeSeriesChartProps = {
  data: DashboardEmailTimeSeries;
};

export function EmailTimeSeriesChart({data}: EmailTimeSeriesChartProps) {
  const {chartConfig, chartData, seriesKeys} = useMemo(() => {
    const series = (data.series ?? []).map((s, idx) => ({
      ...s,
      chartKey: `${s.key || "series"}-${idx}`,
      color: TIME_SERIES_COLORS[idx % TIME_SERIES_COLORS.length],
    }));
    const config: ChartConfig = Object.fromEntries(
      series.map((s) => [
        s.chartKey,
        {
          label: s.label || s.key,
          color: s.color,
        },
      ]),
    );
    const rows = data.buckets_local.map((bucket, i) => {
      const row: Record<string, string | number> = {bucket};
      series.forEach((s) => {
        row[s.chartKey] = s.data[i] ?? 0;
      });
      return row;
    });
    return {chartConfig: config, chartData: rows, seriesKeys: series.map((s) => s.chartKey)};
  }, [data]);

  if (chartData.length === 0 || seriesKeys.length === 0) {
    return (
      <Card className="gap-3 py-4">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Email volume</CardTitle>
        </CardHeader>
        <div className="px-6 pb-4">
          <p className="text-muted-foreground text-sm">No email activity for this period.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="gap-3 py-4">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Email volume</CardTitle>
      </CardHeader>
      <div className="px-2 pb-4 sm:px-6">
        <ChartContainer config={chartConfig} className="aspect-auto h-[220px] w-full">
          <BarChart accessibilityLayer data={chartData} margin={{left: 0, right: 8}}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="bucket"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={formatBucketTick}
            />
            <YAxis tickLine={false} axisLine={false} width={32} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => formatBucketLong(String(value))}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent />} />
            {seriesKeys.map((key) => (
              <Bar
                key={key}
                dataKey={key}
                fill={chartConfig[key]?.color as string}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ChartContainer>
      </div>
    </Card>
  );
}
